/**
 * insets — edge-to-edge 안전영역(safe-area) 배선. (Android / iOS 공통 진입점)
 *
 * 앱은 전 화면 edge-to-edge 다 (2026-08-20 — 상태바·내비게이션 바 등 기기 메뉴 영역까지
 * WebView 가 그린다). 콘텐츠 침범 보정은 전부 CSS 의 --safe-area-* 변수가 담당한다
 * (styles/safe-area.css 가 env() 기본값 정의, 플로팅 앱바/하단탭 등이 소비).
 *
 *  - iOS: contentInset:'never' + viewport-fit=cover 라 env(safe-area-inset-*) 가
 *    실값으로 채워진다 → CSS 기본값이 그대로 동작. 여기서 할 일 없음.
 *  - Android: WebView 의 env() 는 시스템 바를 반영하지 않는다(항상 0). MainActivity
 *    가 관측한 인셋을 커스텀 Insets 플러그인으로 받아 documentElement 인라인 스타일로
 *    --safe-area-* 를 덮어쓴다 (initSafeAreaInsets — 인라인이 stylesheet 의 env 기본값을
 *    이긴다). 회전/키보드 등 인셋 변경은 safeAreaChanged 이벤트로 따라간다.
 *  - web 에서는 no-op (env 기본값 = 0).
 *
 * 바 배경이 앱 배경 그 자체가 됐으므로, 시스템 바 아이콘 색은 테마 모드를 따라가야
 * 한다 — syncSystemBarsWithTheme 를 테마 모드 변경 시 호출한다 (AppTheme.tsx).
 */
import { registerPlugin } from "@capacitor/core";
import { getPlatform, isNative, nativeWarn } from "./index";
import { setStatusBar } from "./statusBar";

interface SafeArea {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface InsetsNativePlugin {
  getSafeArea(): Promise<SafeArea>;
  /** lightContent=true = 흰 아이콘(다크 배경용). 상태바+내비 바 동시 적용. */
  setSystemBarsAppearance(options: { lightContent: boolean }): Promise<void>;
  addListener(
    eventName: "safeAreaChanged",
    listener: (area: SafeArea) => void,
  ): Promise<{ remove: () => Promise<void> }>;
}

const Insets = registerPlugin<InsetsNativePlugin>("Insets");

function applySafeArea(area: SafeArea): void {
  const style = document.documentElement.style;
  style.setProperty("--safe-area-top", `${Math.max(0, area.top)}px`);
  style.setProperty("--safe-area-right", `${Math.max(0, area.right)}px`);
  style.setProperty("--safe-area-bottom", `${Math.max(0, area.bottom)}px`);
  style.setProperty("--safe-area-left", `${Math.max(0, area.left)}px`);
}

/**
 * Android 전용 — 네이티브가 관측한 시스템 바 인셋으로 --safe-area-* CSS 변수를 채운다.
 * 앱 부팅(main.tsx)에서 1회 호출. iOS/web 은 no-op (env() 가 담당).
 */
export async function initSafeAreaInsets(): Promise<void> {
  if (!isNative() || getPlatform() !== "android") return;
  try {
    await Insets.addListener("safeAreaChanged", applySafeArea);
    applySafeArea(await Insets.getSafeArea());
  } catch (err) {
    nativeWarn("insets:init", err);
  }
}

/**
 * 테마 모드에 맞춰 시스템 바 아이콘 색을 동기화한다. dark 모드 = 흰 아이콘.
 * (edge-to-edge 라 바 배경 = 앱 배경 — 라이트 모드에서 흰 아이콘이면 안 보인다)
 */
export async function syncSystemBarsWithTheme(mode: "light" | "dark"): Promise<void> {
  if (!isNative()) return;
  try {
    // 상태바 아이콘 — iOS/Android 공통 (StatusBar 플러그인).
    // 래퍼의 style 은 "원하는 아이콘 색" 기준: dark 모드 → light(흰) 아이콘.
    await setStatusBar({ style: mode === "dark" ? "light" : "dark" });
    if (getPlatform() === "android") {
      // 내비게이션 바(제스처/3버튼) 아이콘은 StatusBar 플러그인이 못 만진다.
      await Insets.setSystemBarsAppearance({ lightContent: mode === "dark" });
    }
  } catch (err) {
    nativeWarn("insets:barTheme", err);
  }
}
